import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import useDebounce from '../../../hooks/useDebounce';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useAlert } from '../../../context/alert';
import { useTopicsInfiniteQuery, useDeleteTopicsMutation, useSetTopicsVisibilityMutation } from '../../../hooks/useForumQuery';
import { fetchCategory } from '../../../services/categories';
import { fetchSubcategories } from '../../../services/catalogs';
import { CategoryResponse } from '../../../types/api/categories';
import { SubcategoryResponse, SubcategoriesListRequest } from '../../../types/api/catalogs';
import { TopicResponse } from '../../../types/api/forum';
import { pageSize } from '../../../services/settings';
import { TopicView } from './components/TopicView';
import { renderTruncatedText } from '../../../helpers/strings';
import { Placeholder } from '../../basic';
import RegularPageLayout from '../../../layouts/RegularPageLayout';
import TableActionsBanner from '../../common/TableActionsBanner';
import { Stack, TextInput, Button, SkeletonBox, Breadcrumbs, Spinner } from '@primer/react';
import { SearchIcon, XCircleFillIcon, XCircleIcon, XIcon } from '@primer/octicons-react'

const EMPTY_SET = new Set<string>();

export default function TopicsList() {

    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [fetchedId, setFetchedId] = useState<string | undefined>(undefined);
    const listRef = useRef<HTMLDivElement | null>(null);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);
    const [selectedSubcategory, setSelectedSubcategory] = useState<SubcategoryResponse | undefined>(undefined);
    const [parentCategory, setParentCategory] = useState<CategoryResponse | undefined>(undefined);
    const [subcategories, setSubcategories] = useState<SubcategoryResponse[]>([]);
    const [selectedTopic, setSelectedTopic] = useState<TopicResponse | undefined>(undefined);

    const { setSuccess, setInfo, setError, addCustomAlert, updateAlert, removeAlert } = useAlert();

    // Dynamic header height helper
    const [headerHeight, setHeaderHeight] = useState(0);
    const headerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (headerRef.current) {
            const resizeObserver = new ResizeObserver(() => {
                if (headerRef.current) {
                    setHeaderHeight(headerRef.current.offsetHeight);
                }
            });

            resizeObserver.observe(headerRef.current);
            setHeaderHeight(headerRef.current.offsetHeight);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [subcategories]);

    const [searchQuery, setSearchQuery] = useState<string>('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500)

    const { mutate: deleteTopicRequest, isPending: isDeletingProcess } = useDeleteTopicsMutation(() => {
        topicDidDeleted();
    });

    const { mutate: setTopicsVisibilityRequest, isPending: isVisibilityProcess } = useSetTopicsVisibilityMutation();

    // Select
    const [selectedRows, setSelectedRows] = useState<Set<string>>(EMPTY_SET);

    useEffect(() => {
        setSelectedRows(EMPTY_SET);
        setSelectedTopic(undefined)
    }, [debouncedSearchQuery]);

    const { mutate: fetchCategoryById, isPending: isLoadingCategory } = useMutation({
        mutationFn: fetchCategory,
        onSuccess: (data) => {
            setParentCategory(data)
        }
    });
    const { mutate: fetchSubcategoriesForParent, isPending: isLoadingSubcategory } = useMutation({
        mutationFn: (params: SubcategoriesListRequest) => fetchSubcategories(params),
        onSuccess: (data) => {
            setSubcategories(data.list)
            if (data.list.length > 0) {
                setSelectedSubcategory(data.list[0])
            }
        }
    });

    useEffect(() => {
        const id = searchParams.get('id');

        if (id && fetchedId !== id && !isLoadingCategory) {
            setFetchedId(id);
            fetchCatalogData();
        }
    }, [searchParams, fetchedId, isLoadingCategory]);

    useEffect(() => {
        dismissTableActionAlert()
        setSelectedRows(EMPTY_SET);
        setSelectedTopic(undefined)
    }, [selectedSubcategory]);

    const fetchCatalogData = () => {
        const id = searchParams.get('id');
        if (id) {
            fetchCategoryById(id);
        }
    }

    useEffect(() => {
        if (parentCategory) {
            const params = {
                category_id: parentCategory.id,
                search: "",
            }
            fetchSubcategoriesForParent(params)
        }
    }, [parentCategory]);

    const {
        isLoading: isLoadingTopics,
        data: sectionsData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        refetch,
        isFetched
    } = useTopicsInfiniteQuery(
        debouncedSearchQuery,
        pageSize,
        selectedSubcategory?.id, //catalogId?: string,
        fetchedId, //categoryId?: string,
        undefined, // createdFrom?: string,
        undefined,  //createdTo
        undefined,  //userId
        undefined, //ordering
        {
            enabled: fetchedId !== undefined && selectedSubcategory !== undefined
        }
    );

    const isLoading = isLoadingCategory || isLoadingSubcategory || isLoadingTopics

    const clearSearchInput = () => {
        setSearchQuery("")
    }

    const handleScroll = useInfiniteScroll(
        listRef,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        sectionsData
    );

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const dismissConfirmBlockAlert = useCallback(() => {
        setActionsAlertId(undefined)
        dismissTableActionAlert()
        setSelectedRows(EMPTY_SET);
        setSelectedTopic(undefined)
    }, [actionsAlertId]);

    const openTopicPage = (topic: TopicResponse) => {
        if (parentCategory && selectedSubcategory) {
            navigate(`/forums/forumComments/?topicID=${topic.id}&parentCategoryID=${parentCategory.id}&subcategoryID=${selectedSubcategory.id}`)
        }
    }

    const deleteTopicAction = (topic: TopicResponse) => {
        showDeleteTopicAlert(topic)
    }

    // Скрытие темы из публичной выдачи. В админке тема остаётся видимой (ALX-60296)
    const toggleTopicVisibilityAction = (topic: TopicResponse) => {
        setTopicsVisibilityRequest(
            { ids: [topic.id], isHidden: !topic.isHidden },
            {
                onSuccess: () => setInfo(topic.isHidden ? 'Тема снова видна' : 'Тема скрыта'),
            }
        )
    }

    useEffect(() => {
        let selectedItemsCount = selectedRows.size

        if (selectedItemsCount > 0) {
            handleAddTableAlert(Array.from(selectedRows))
        } else if (actionsAlertId) {
            if (selectedTopic) {
                return
            }
            removeAlert(actionsAlertId)
            setActionsAlertId(undefined)
        }
    }, [selectedRows])

    const showDeleteTopicAlert = useCallback((topic: TopicResponse) => {
        dismissConfirmBlockAlert()
        setSelectedTopic(topic)
        let props = createConfirmDeleteAlertProps([], topic)
        const alertId = addCustomAlert(
            TableActionsBanner,
            props,
            {
                autoClose: false,
            }
        );

        setActionsAlertId(alertId)
        actionsAlertIdRef.current = alertId
    }, [actionsAlertId]);

    const topicDidDeleted = () => {
        if (selectedRows.size > 1) {
            setInfo('Темы удалены')
        } else {
            setInfo('Тема удалена')
        }

        refetch()
        dismissConfirmBlockAlert()
    }

    const handleAddTableAlert = useCallback((ids: string[]) => {
        let props = createConfirmDeleteAlertProps(ids, undefined)

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );

            setActionsAlertId(alertId)
        }
    }, [actionsAlertId, sectionsData]);

    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {
        rowSelect(rowId, isSelected)
    }, [handleAddTableAlert]);

    const rowSelect = useCallback((rowId: string, isSelected: boolean) => {
        setSelectedRows(prev => {
            const newSelection = new Set(prev);
            if (isSelected) {
                newSelection.add(rowId);
            } else {
                newSelection.delete(rowId);
            }
            return newSelection;
        });

    }, [handleAddTableAlert]);

    // TABLE ACTIONS ALERT
    function createConfirmDeleteAlertProps(ids: string[], topic: TopicResponse | undefined) {
        const buttons = [
            <Button key="no-button" variant="default" onClick={dismissConfirmBlockAlert}>
                Нет
            </Button>
        ];

        if (topic) {
            buttons.push(
                <Button key="yes-button" variant="danger" disabled={isDeletingProcess} onClick={() => deleteSelectedTopics([topic.id])}>
                    Да, удалить
                </Button>
            );
        } else {
            buttons.push(
                <Button key="yes-button" variant="danger" disabled={isDeletingProcess} onClick={() => deleteSelectedTopics(ids)}>
                    Да, удалить
                </Button>
            );
        }
        const count = topic !== undefined ? 1 : ids.length
        return {
            count: count,
            title: count > 1 ? 'Точно хотите удалить?' : 'Точно хотите удалить тему?',
            buttons: buttons,
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined);
                setSelectedRows(EMPTY_SET);
                setSelectedTopic(undefined)
            }
        };
    }

    const deleteSelectedTopics = (ids: string[]) => {
        deleteTopicRequest({ ids: ids });
    };

    const emptyListDescription = `В сервисе не создано ни одного
     форума по этому подразделу`;
    const emptySearchListDescription = `По вашему запросу не найдено
    подходящих форумов`;

    return (
        <RegularPageLayout
            header={
                <Stack ref={headerRef}>
                    <Stack justify="space-between" direction="horizontal">
                        <Breadcrumbs>
                            <Breadcrumbs.Item href="/forums/">Все разделы</Breadcrumbs.Item>
                            <Breadcrumbs.Item href="#" selected>
                                {renderTruncatedText(parentCategory?.title, 32)}
                            </Breadcrumbs.Item>
                        </Breadcrumbs>
                        <Stack direction="horizontal">
                            <TextInput
                                leadingVisual={SearchIcon}
                                trailingAction={
                                    searchQuery.length > 0 ? (
                                        <TextInput.Action
                                            onClick={() => {
                                                setSearchQuery('');
                                            }}
                                            icon={XCircleFillIcon}
                                            aria-label="Очистить"
                                        />
                                    ) : undefined
                                }
                                value={searchQuery}
                                placeholder='Поиск'
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                }}
                            />
                        </Stack>
                    </Stack>
                    <div className="flex flex-wrap gap-2 justify-start">
                        {isLoadingSubcategory && <Spinner />}
                        {subcategories.length > 0 && subcategories.map((subcategory, index) => (
                            <button
                                key={index}
                                onClick={() => setSelectedSubcategory(subcategory)}
                                className={`flex items-center gap-[4px] px-2 py-[6px] border-[1px]
        ${selectedSubcategory?.id === subcategory.id ? "bg-aloix-black hover:bg-gray-800" : "bg-white hover:bg-gray-50 border-border"}
        rounded-md h-8`}>
                                <span className={`${selectedSubcategory?.id === subcategory.id ? "text-white" : "text-aloix-black"} font-medium text-xs`}>
                                    {renderTruncatedText(subcategory.title, 32)}
                                </span>
                            </button>
                        ))}
                    </div>
                </Stack>
            }
            content={
                <Stack>
                    <div
                        ref={listRef}
                        onScroll={handleScroll}
                        style={{ marginTop: `${headerHeight - 48}px` }}
                        className="overflow-y-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-4 p-4">
                        {sectionsData?.pages.map((page, i) => (
                            <React.Fragment key={i}>
                                {page.list.map((e: TopicResponse, index) => (
                                    <TopicView
                                        key={e.id}
                                        topic={e}
                                        isSelected={selectedRows.has(e.id)}
                                        isDisabled={isDeletingProcess || isVisibilityProcess}
                                        didSelected={(isSelected) => handleRowSelect(e.id, isSelected)}
                                        didOpenTopic={() => openTopicPage(e)}
                                        didDelete={() => deleteTopicAction(e)}
                                        didToggleVisibility={() => toggleTopicVisibilityAction(e)}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                    {isFetched && !isLoading && !isFetchingNextPage && (sectionsData?.pages?.flatMap(page => page.list).length ?? 0) === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header={searchQuery.length > 0 ? 'Ничего не было найдено' : 'Форумы не найдены'}
                            description={searchQuery.length > 0 ? emptySearchListDescription : emptyListDescription}
                            button={searchQuery.length > 0 && <Button
                                variant="default"
                                leadingVisual={XIcon}
                                onClick={clearSearchInput}>
                                Очистить</Button>}
                        />
                    )}
                    {/*Индикатор загрузки*/}
                    {(isFetchingNextPage || isLoadingTopics) &&
                        <div className="max-h-screen overflow-y-auto">
                            <SkeletonBox height="4rem" />
                        </div>
                    }
                </Stack>
            }
        >
        </RegularPageLayout>
    )
}